import {
  BarcodeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  DesktopOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  HistoryOutlined,
  RightOutlined,
  UserOutlined,
  WifiOutlined,
  WindowsOutlined,
} from '@ant-design/icons'
import { Collapse, Menu } from 'antd'
import type { ItemType } from 'antd/es/menu/interface'
import { usePermissions } from '@kolab/dms-base/hooks'
import type { Device } from '../../../types/device.types'
import DeviceStatusBadge from '../DeviceStatusBadge'
import { P } from '../../../rbac'

interface DeviceDetailSidebarProps {
  device: Device
  activeSection: string
  onSectionChange: (section: string) => void
  onDelete: () => void
}

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
      <span
        style={{ color: '#6366f1', fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}
      >
        {icon}
      </span>
      <span style={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {label}
      </span>
      {value && (
        <span
          style={{
            fontSize: 13,
            color: '#1e293b',
            fontWeight: 500,
            marginLeft: 'auto',
            minWidth: 0,
            textAlign: 'right',
            wordBreak: 'break-word',
          }}
        >
          {value}
        </span>
      )}
    </div>
  )
}

export default function DeviceDetailSidebar({
  device,
  activeSection,
  onSectionChange,
  onDelete: _onDelete,
}: DeviceDetailSidebarProps) {
  const { hasPermission } = usePermissions()
  const info = device.pingInfo

  const lastConnectionDate = device.lastPingAt ? new Date(device.lastPingAt) : null

  const computerName = info?.ComputerName || device.device_id
  const osVersion = info?.OSVersion || null
  const userName = info?.UserName || null
  const publicIp = info?.PublicIpAddress || device.ipAddress
  const serialNumber = info?.BIOSInfo?.SerialNumber || device.device_id
  const wifiAdapter = info?.NetworkAdapters?.find(
    (a) => a.Type === 'Wireless80211' || /wi-?fi/i.test(a.Name || '')
  )
  const wifiLabel = wifiAdapter?.Description || wifiAdapter?.Name || null

  return (
    <div
      style={{
        width: 360,
        minWidth: 360,
        background: '#fff',
        borderRight: '1px solid #e2e8f0',
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DesktopOutlined style={{ color: '#fff', fontSize: 16 }} />
          </div>
          <div>
            <h2
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#1e293b',
                margin: 0,
                letterSpacing: '-0.01em',
              }}
            >
              {computerName}
            </h2>
            <DeviceStatusBadge isOnline={device.isOnline} />
          </div>
        </div>

        {osVersion && (
          <p style={{ fontSize: 13, color: '#475569', margin: '4px 0 0' }}>{osVersion}</p>
        )}

        {lastConnectionDate && (
          <div style={{ marginTop: 10, fontSize: 13, color: '#475569' }}>
            <span style={{ marginRight: 4 }}>Last connection:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <CalendarOutlined style={{ fontSize: 12, color: '#6366f1' }} />
                {lastConnectionDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ClockCircleOutlined style={{ fontSize: 12, color: '#6366f1' }} />
                {lastConnectionDate.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Collapsible Sections */}
      <div style={{ flex: 1 }}>
        <Collapse
          defaultActiveKey={['device-info']}
          ghost
          expandIcon={({ isActive }) => (
            <RightOutlined rotate={isActive ? 90 : 0} style={{ fontSize: 10, color: '#94a3b8' }} />
          )}
          style={{ background: 'transparent' }}
          items={[
            {
              key: 'device-info',
              label: (
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Device info</span>
              ),
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <InfoItem
                    icon={<WindowsOutlined />}
                    label={osVersion || '--'}
                    value={info?.Is64BitOS ? '64-bit' : null}
                  />
                  <InfoItem
                    icon={<BarcodeOutlined />}
                    label="Serial No:"
                    value={
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          wordBreak: 'break-all',
                        }}
                      >
                        {serialNumber}
                      </span>
                    }
                  />
                  <InfoItem icon={<WifiOutlined />} label="Wi-Fi:" value={wifiLabel || '--'} />
                  <InfoItem icon={<UserOutlined />} label="Active user:" value={userName || '--'} />
                  <InfoItem
                    icon={<GlobalOutlined />}
                    label="IP Address:"
                    value={
                      publicIp ? (
                        <span
                          style={{
                            fontSize: 11,
                            color: '#6366f1',
                            background: '#eef2ff',
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                          }}
                        >
                          {publicIp}
                        </span>
                      ) : (
                        '--'
                      )
                    }
                  />
                </div>
              ),
            },
            {
              key: 'labels',
              label: (
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Labels</span>
              ),
              children: (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No labels assigned</p>
              ),
            },
            {
              key: 'custom-fields',
              label: (
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                  Custom fields
                </span>
              ),
              children: (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No custom fields</p>
              ),
            },
          ]}
        />

        {/* Administration Section */}
        <div style={{ padding: '8px 16px 0' }}>
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#1e293b',
              margin: '0 0 4px',
              paddingLeft: 8,
            }}
          >
            Administration
          </p>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[activeSection]}
          onClick={({ key }) => onSectionChange(key)}
          style={{ border: 'none', background: 'transparent' }}
          items={([
            hasPermission(P.device_operations.geolocation.view) && {
              key: 'map-actions',
              icon: <EnvironmentOutlined />,
              label: (
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span>Map and Actions</span>
                  <RightOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                </div>
              ),
            },
            hasPermission(P.device_operations.activity_log.view) && {
              key: 'activity-log',
              icon: <HistoryOutlined />,
              label: (
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span>Activity Log</span>
                  <RightOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                </div>
              ),
            },
            hasPermission(P.device_operations.command_log.view) && {
              key: 'command-log',
              icon: <CodeOutlined />,
              label: (
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span>Command Log</span>
                  <RightOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                </div>
              ),
            },
            {
              key: 'hardware-info',
              icon: <DesktopOutlined />,
              label: (
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span>Hardware Information</span>
                  <RightOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                </div>
              ),
            },
          ] as (ItemType | false)[]).filter(Boolean) as ItemType[]}
        />
      </div>
    </div>
  )
}
